import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { authService } from '@/services/auth-service';
import { entraIdService } from '@/services/entra-id-service';
import { useAuthConfig } from '@/hooks/use-auth-config';
import type {
  AdminUser,
  LoginCredentials,
  AuthToken,
  UserSession,
  UserRole,
  RolePermissions,
} from '@/types';
import {
  resolvePermissions,
  getAccessLevel as rbacGetAccessLevel,
  LEGACY_ROLE_MAP,
} from '@shared/rbac';
import type {
  PermissionKey,
  UserRole as RbacUserRole,
  AccessLevel,
} from '@shared/rbac';
import { LEGACY_ROLE_PERMISSIONS } from './legacy-permissions';

// ---------------------------------------------------------------------------
// Dev auth bypass — seeds a mock super_admin session for local testing.
// Enable via VITE_DEV_AUTH_BYPASS=true in .env (never in production).
// ---------------------------------------------------------------------------

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

const DEV_USER: AdminUser = {
  id: 'dev-local-user',
  username: 'dev@localhost',
  email: 'dev@localhost',
  firstName: 'Dev',
  lastName: 'Admin',
  role: 'super_admin',
};

const DEV_TOKEN: AuthToken = {
  accessToken: 'dev-bypass-token',
  refreshToken: 'dev-bypass-refresh',
  expiresIn: 86400,
  tokenType: 'Bearer',
};

const DEV_SESSION: UserSession = {
  user: DEV_USER,
  token: DEV_TOKEN,
  expiresAt: Date.now() + 86400 * 1000,
  lastActivity: Date.now(),
  authProvider: 'local',
};

// ---------------------------------------------------------------------------
// RBAC role resolution helper
// ---------------------------------------------------------------------------

/** Valid new-style RBAC roles for direct matching. */
const VALID_RBAC_ROLES: readonly RbacUserRole[] = [
  'super_admin',
  'admin',
  'normal_user',
  'finance_user',
  'developer',
];

/**
 * Resolve a frontend UserRole (which may be a legacy value) to a new-style
 * RbacUserRole suitable for the shared RBAC module.
 *
 * Resolution order:
 *   1. Direct match against the 5 new roles
 *   2. Legacy map lookup (admin→admin, support→normal_user, finance→finance_user, viewer→normal_user)
 *   3. Fallback to 'normal_user'
 */
function resolveRbacRole(role: UserRole): RbacUserRole {
  if ((VALID_RBAC_ROLES as readonly string[]).includes(role)) {
    return role as RbacUserRole;
  }
  return LEGACY_ROLE_MAP[role] ?? 'normal_user';
}

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

interface AuthContextValue {
  user: AdminUser | null;
  session: UserSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: UserRole;

  // --- Legacy boolean permissions (deprecated) ---
  /** @deprecated Use `checkPermission()` with a PermissionKey instead. */
  permissions: RolePermissions;
  /** @deprecated Use `checkPermission()` with a PermissionKey instead. */
  hasPermission: (permission: keyof RolePermissions) => boolean;

  // --- Granular RBAC permissions ---
  /** All resolved RBAC permission keys for the current user's role. */
  rbacPermissions: Set<PermissionKey>;
  /** Returns true when the user's role has any access level other than 'not_allowed'. */
  checkPermission: (key: PermissionKey) => boolean;
  /** Returns true when the user has at least one of the listed permissions. */
  hasAnyPermission: (keys: PermissionKey[]) => boolean;
  /** Returns true when the user has all of the listed permissions. */
  hasAllPermissions: (keys: PermissionKey[]) => boolean;
  /** Returns the AccessLevel for a given permission key. */
  getAccessLevel: (key: PermissionKey) => AccessLevel;

  login: (credentials: LoginCredentials) => Promise<AuthToken>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// --- Provider ---

interface AuthProviderProps {
  children: ReactNode;
  onSessionExpired?: () => void;
}

export function AuthProvider({ children, onSessionExpired }: AuthProviderProps) {
  const [user, setUser] = useState<AdminUser | null>(() => {
    if (DEV_AUTH_BYPASS) return DEV_USER;
    const existing = authService.getSession();
    return existing?.user ?? null;
  });
  const [session, setSession] = useState<UserSession | null>(() => {
    if (DEV_AUTH_BYPASS) return DEV_SESSION;
    return authService.getSession();
  });
  const [isLoading] = useState(false);

  // Fetch auth config for Entra ID tenant ID (needed for logout URL)
  const { config: authConfig } = useAuthConfig();
  const authConfigRef = useRef(authConfig);
  useEffect(() => {
    authConfigRef.current = authConfig;
  }, [authConfig]);

  // Start activity tracking on mount if session exists
  useEffect(() => {
    if (session) {
      authService.startActivityTracking();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register session-expired callback
  useEffect(() => {
    authService.onSessionExpired(() => {
      setUser(null);
      setSession(null);
      onSessionExpired?.();
    });
  }, [onSessionExpired]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthToken> => {
    const token = await authService.login(credentials);
    // Store access token for api-client interceptor
    sessionStorage.setItem('xs_auth_token', token.accessToken);
    const restored = authService.getSession();
    if (restored) {
      setSession(restored);
      setUser(restored.user);
    }
    return token;
  }, []);

  /**
   * Logout flow with Entra ID single sign-out support.
   *
   * - For Entra ID users: clears local session, then redirects to Entra ID
   *   logout endpoint with post_logout_redirect_uri pointing to /login.
   * - For local users: existing logout flow unchanged.
   * - Handles Entra ID logout redirect failure gracefully (still clears
   *   local session and redirects to /login).
   *
   * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 9.3
   */
  const logout = useCallback(async () => {
    const { authProvider } = await authService.logout();
    sessionStorage.removeItem('xs_auth_token');
    setUser(null);
    setSession(null);

    // For Entra ID users, redirect to Entra ID logout for single sign-out
    if (authProvider === 'entra_id') {
      const tenantId = authConfigRef.current?.tenantId;
      const postLogoutUri = `${window.location.origin}/login`;

      if (tenantId) {
        try {
          const logoutUrl = entraIdService.getLogoutUrl(tenantId, postLogoutUri);
          window.location.href = logoutUrl;
          return;
        } catch {
          // Graceful degradation: if redirect construction fails,
          // fall through to local redirect below (Req 7.5)
        }
      }

      // Fallback: redirect to login page if tenantId unavailable or redirect failed
      window.location.href = '/login';
    }
  }, []);

  const role: UserRole = user?.role ?? 'viewer';
  const permissions = LEGACY_ROLE_PERMISSIONS[role];

  // --- Legacy boolean permission check (deprecated) ---
  const hasPermission = useCallback(
    (permission: keyof RolePermissions): boolean => {
      return permissions[permission];
    },
    [permissions],
  );

  // --- Granular RBAC permission resolution ---
  const rbacRole = useMemo(() => resolveRbacRole(role), [role]);

  const rbacPermissions = useMemo<Set<PermissionKey>>(
    () => resolvePermissions(rbacRole),
    [rbacRole],
  );

  const checkPermission = useCallback(
    (key: PermissionKey): boolean => rbacPermissions.has(key),
    [rbacPermissions],
  );

  const hasAnyPermission = useCallback(
    (keys: PermissionKey[]): boolean => keys.some((key) => rbacPermissions.has(key)),
    [rbacPermissions],
  );

  const hasAllPermissions = useCallback(
    (keys: PermissionKey[]): boolean => keys.every((key) => rbacPermissions.has(key)),
    [rbacPermissions],
  );

  const getAccessLevel = useCallback(
    (key: PermissionKey): AccessLevel => rbacGetAccessLevel(rbacRole, key),
    [rbacRole],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      isAuthenticated: user !== null && session !== null,
      isLoading,
      role,
      permissions,
      hasPermission,
      rbacPermissions,
      checkPermission,
      hasAnyPermission,
      hasAllPermissions,
      getAccessLevel,
      login,
      logout,
    }),
    [
      user,
      session,
      isLoading,
      role,
      permissions,
      hasPermission,
      rbacPermissions,
      checkPermission,
      hasAnyPermission,
      hasAllPermissions,
      getAccessLevel,
      login,
      logout,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

// --- Hook ---

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
